import { Directive, HostBinding, Input, OnDestroy, OnInit, inject, isDevMode } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { AGENT_CATALOG, agentTargetValue, isKnownAnchor } from './agent-catalog';
import { AgentFormRegistry } from './agent-form.registry';

/**
 * The three markers that make the portal readable for the in-house agent
 * (features/docs/technical/portal-agent-native.md). They only set attributes
 * or register a form — no behaviour, no styling.
 *
 * Put them on the shared building blocks where possible (table rows, tiles,
 * setting rows), so a new page is marked without extra work.
 */

const warnedAnchors = new Set<string>();

/** `alAnchor="agents.create"` — a catalog anchor the agent can point at. */
@Directive({ selector: '[alAnchor]' })
export class AlAnchorDirective {
  @Input() alAnchor: string | null | undefined;

  @HostBinding('attr.data-al-anchor')
  get anchorAttr(): string | null {
    const id = this.alAnchor || null;
    if (id && isDevMode() && !warnedAnchors.has(id) && !isKnownAnchor(id)) {
      warnedAnchors.add(id); // a getter runs on every change detection — warn once
      console.warn(`[agent] anchor "${id}" is not in portal-ui-catalog.json`);
    }
    return id;
  }
}

/** `[agentTarget]="['bot', t.id, 'card']"` — an object the agent can highlight after acting on it. */
@Directive({ selector: '[agentTarget]' })
export class AgentTargetDirective {
  @Input() agentTarget: readonly [string, unknown, string] | null | undefined;

  @HostBinding('attr.data-agent-target')
  get targetAttr(): string | null {
    if (!this.agentTarget) return null;
    const [type, id, element] = this.agentTarget;
    return agentTargetValue(type, id, element);
  }
}

/**
 * `<form [formGroup]="form" alAgentForm="tutor-wizard.basics">` — a form the
 * agent may prefill. The id must be in the catalog's `forms`; the agent
 * never submits.
 */
@Directive({ selector: '[alAgentForm]' })
export class AgentFormDirective implements OnInit, OnDestroy {
  @Input() alAgentForm = '';

  private readonly formGroup = inject(FormGroupDirective, { self: true, optional: true });
  private readonly registry = inject(AgentFormRegistry);
  private unregister?: () => void;

  ngOnInit(): void {
    if (!this.alAgentForm || !this.formGroup) return;
    if (!(this.alAgentForm in AGENT_CATALOG.forms)) {
      if (isDevMode()) console.warn(`[agent] form "${this.alAgentForm}" is not in portal-ui-catalog.json`);
      return;
    }
    this.unregister = this.registry.register(this.alAgentForm, () => this.formGroup?.form ?? null);
  }

  ngOnDestroy(): void {
    this.unregister?.();
  }
}
